import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from 'react-oidc-context'
import { fetchPreference, savePreference } from '../api/userPreferences'
import {
  isThemePreference,
  readThemePreference,
  resolveEffectiveTheme,
  systemPrefersDarkMedia,
  systemPrefersDarkNow,
  writeThemePreference,
  type EffectiveTheme,
  type ThemePreference,
} from './themePreference'

export type { EffectiveTheme, ThemePreference }

interface ThemeContextValue {
  /** The user's stored choice — `light` | `dark` | `system`. */
  preference: ThemePreference
  /** The concrete theme actually applied right now (`system` resolved against the OS). */
  effectiveTheme: EffectiveTheme
  setPreference: (next: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/**
 * Theme controller (roadmap #11). Resolves `light | dark | system` to a
 * concrete theme and stamps `data-theme` on `<html>` — the same attribute
 * `tokens.css`'s `[data-theme="dark"]` override block and the inline
 * bootstrap script in `index.html` key off, so there is exactly one
 * mechanism for "what theme is active" end to end.
 *
 * CSS custom properties do the rest: no component needs to re-render on a
 * theme switch (design-system.md §2.1), this provider only needs to keep
 * `<html data-theme>` correct and re-run when the OS preference changes
 * while `system` is selected.
 *
 * <p><b>IB-292 server-sync delta.</b> `localStorage` (read synchronously above, and by the inline
 * bootstrap script in `index.html`) stays the FAST first-paint source — never blocked on a network
 * round trip. Once authenticated, this provider additionally hydrates from the real per-user
 * preference endpoint (`GET /me/preferences/theme`) and, if the server holds a DIFFERENT value,
 * applies it (so the choice follows the user across devices) and mirrors it back into
 * `localStorage`. Every `setPreference` still writes `localStorage` synchronously AND best-effort
 * PUTs the server (`api/userPreferences.ts` never throws on a network failure — this provider must
 * never break on one).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readThemePreference())
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => systemPrefersDarkNow())
  const auth = useAuth()
  const isAuthenticated = auth?.isAuthenticated ?? false
  const hydratedRef = useRef(false)

  useEffect(() => {
    const media = systemPrefersDarkMedia()
    if (!media) return undefined
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const effectiveTheme = resolveEffectiveTheme(preference, systemPrefersDark)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme)
  }, [effectiveTheme])

  useEffect(() => {
    if (!isAuthenticated || hydratedRef.current) return
    hydratedRef.current = true
    const controller = new AbortController()
    void fetchPreference('theme', controller.signal).then((serverValue) => {
      if (isThemePreference(serverValue) && serverValue !== readThemePreference()) {
        setPreferenceState(serverValue)
        writeThemePreference(serverValue)
      }
    })
    return () => controller.abort()
  }, [isAuthenticated])

  const isAuthenticatedRef = useRef(isAuthenticated)
  isAuthenticatedRef.current = isAuthenticated

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    writeThemePreference(next)
    // Only sync to the per-user endpoint once we actually know who "you" are — an unauthenticated
    // write has no caller identity to attach to and would just 401.
    if (isAuthenticatedRef.current) {
      void savePreference('theme', next)
    }
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, effectiveTheme, setPreference }),
    [preference, effectiveTheme, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/** Throws outside a `ThemeProvider` — a missing provider is a wiring bug, not a state to degrade into. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme() must be called within a <ThemeProvider>')
  }
  return ctx
}
